import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DocumentTextIcon,
  AcademicCapIcon,
  SparklesIcon,
  DocumentArrowUpIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  ClockIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { useNotes } from "../context/NotesContext";
import { useTest } from "../context/TestContext";
import { apiService } from "../services/apiService.js";
import { aiService } from "../services/aiService.js";
import { useError } from "../context/ErrorContext";
import Sidebar from "../components/Sidebar";
import { fileProcessingService } from "../services/fileProcessingService";
import { dataService } from "../services/dataService";
import HamburgerButton from "../components/HamburgerButton";
import VideoLoadingOverlay from "../components/VideoLoadingOverlay";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { notes, updateNotes } = useNotes();
  const { createdTests, testResults } = useTest();
  const { showError } = useError();
  const [summarizeInput, setSummarizeInput] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const isCollapsed = localStorage.getItem("sidebar_collapsed") === "true";
  const [isCreatingNotes, setIsCreatingNotes] = useState(false);
  const [testCount, setTestCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState("");
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoLoadingMessages = [
    "Analyzing video content...",
    "Extracting transcripts...",
    "Processing language patterns...",
    "Generating comprehensive notes...",
    "Almost there, finalizing content...",
    "Teaching AI to understand YouTube slang...",
    "Converting 'like and subscribe' into actual content...",
    "Filtering out sponsored segments...",
    "Making sense of creator catchphrases...",
    "Translating YouTube drama into academic content...",
  ];
  const [currentVideoMessage, setCurrentVideoMessage] = useState(0);
  const [isEntering, setIsEntering] = useState(true);

  // Add auth check
  const isAuthenticated = !!localStorage.getItem("auth_token");

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleSummarize = async () => {
    if (!summarizeInput.trim()) return;

    setIsSummarizing(true);
    try {
      showError("Creating summary...", "info");

      console.log("Starting summarization...");
      const response = await apiService.post("/api/ai/summarize", {
        content: summarizeInput,
      });

      console.log("Summarization response:", response);

      if (!response || typeof response !== "object") {
        throw new Error("Invalid response from AI service");
      }

      // Create the note with proper structure
      const noteData = {
        title: response.title || uploadedFileName || "Summary Note",
        content: `${response.summary || ""}\n\n${response.keyPoints?.join(
          "\n"
        )}`,
        folder: "Default",
        keyPoints: response.keyPoints || [],
      };

      // Create note using dataService
      const newNote = await dataService.createNote(noteData);

      if (!newNote || !newNote._id) {
        throw new Error("Failed to create note");
      }

      // Clear the input and filename
      setSummarizeInput("");
      setUploadedFileName("");
      setFileContent("");

      // Show success message
      showError("Summary created successfully!", "success");

      // Add a small delay to ensure the note context is updated
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Navigate to the new note using the ID from the created note
      navigate(`/notes/${newNote._id}`);
    } catch (error) {
      console.error("Summary error details:", error);
      showError(error.message || "Failed to generate summary");
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsProcessingFile(true);
      setUploadedFileName(file.name);

      const isPPT =
        file.name.toLowerCase().endsWith(".ppt") ||
        file.name.toLowerCase().endsWith(".pptx");
      showError(
        isPPT ? "Processing PowerPoint presentation..." : "Processing file...",
        "info"
      );

      const processedText = await fileProcessingService.processFile(file);
      setSummarizeInput(processedText);
      setFileContent(processedText);
    } catch (error) {
      console.error("File processing error:", error);
      showError(error.message || "Failed to process file");
      setUploadedFileName("");
    } finally {
      setIsProcessingFile(false);
    }
  };

  const createNote = async (note) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(note),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create note");
      }

      const newNote = await response.json();
      console.log("New note created:", newNote);

      // Wait a brief moment for context to update
      setTimeout(() => {
        navigate(`/notes/${newNote._id}`);
      }, 100);

      // Optionally, update the notes context directly
      if (updateNotes) {
        updateNotes((prev) => [...prev, newNote]);
      }
    } catch (error) {
      showError("Failed to create note");
      console.error("Note creation error:", error);
    }
  };

  const handleMakeNotes = async () => {
    if (!summarizeInput.trim()) return;

    setIsCreatingNotes(true);
    try {
      showError("Creating detailed notes...", "info");

      const response = await apiService.post("/api/ai/make-notes", {
        content: summarizeInput,
      });

      console.log("Full note creation response:", response);
      console.log("Response title:", response.title);

      if (!response || typeof response !== "object") {
        throw new Error("Invalid response from AI service");
      }

      const noteData = {
        title: "Untitled Note",
        content: response.mainSummary,
        folder: "Default",
        keyPoints: [],
      };

      console.log("Creating note with data:", noteData);
      console.log("Note title being used:", noteData.title);

      const newNote = await dataService.createNote(noteData);
      console.log("Created note:", newNote);

      if (!newNote || !newNote._id) {
        throw new Error("Failed to create note");
      }

      setSummarizeInput("");
      setUploadedFileName("");
      setFileContent("");

      showError("Detailed notes created successfully!", "success");
      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate(`/notes/${newNote._id}`);
    } catch (error) {
      console.error("Note creation error:", error);
      showError(error.message || "Failed to generate detailed notes");
    } finally {
      setIsCreatingNotes(false);
    }
  };

  const handleVideoSummarize = async () => {
    if (!videoUrl.trim()) {
      showError("Please enter a YouTube URL");
      return;
    }
    try {
      setIsProcessingVideo(true);
      setShowVideoModal(false);
      setProgress(25); // Starting progress

      const response = await apiService.summarizeYouTube(videoUrl);
      setProgress(50);

      if (!response || !response.summary) {
        throw new Error("Failed to get video summary");
      }
      setProgress(75);

      // Create note with the summary
      const noteData = {
        title: response.title || "YouTube Video Summary",
        content: response.summary,
        folder: "Default",
        keyPoints: response.keyPoints || [],
        source: videoUrl,
      };

      const newNote = await dataService.createNote(noteData);
      setProgress(100);

      if (!newNote || !newNote._id) {
        throw new Error("Failed to create note");
      }

      setVideoUrl("");
      showError("Video summary created!", "success");

      // Navigate to the new note
      await new Promise((resolve) => setTimeout(resolve, 100));
      navigate(`/notes/${newNote._id}`);
    } catch (error) {
      console.error("Video processing error:", error);
      showError(error.message || "Failed to process video");
    } finally {
      setIsProcessingVideo(false);
      setProgress(0); // Reset progress
    }
  };

  useEffect(() => {
    const loadActivities = async () => {
      if (!isAuthenticated) return; // Don't load if not authenticated

      try {
        // Get test results
        const testResults = await dataService.getTestResults();

        // Transform test results into activity items
        const testActivities = testResults.map((result) => ({
          type: "test",
          title: result.testId?.title || "Untitled Test",
          score: result.score,
          date: new Date(result.createdAt),
          id: result._id,
        }));

        // Transform notes into activity items
        const noteActivities = notes.map((note) => ({
          type: "note",
          title: note.title || "Untitled Note",
          folder: note.folder,
          date: new Date(note.updatedAt),
          id: note._id,
        }));

        // Combine and sort all activities
        const allActivities = [...testActivities, ...noteActivities]
          .sort((a, b) => b.date - a.date)
          .slice(0, 5);

        setRecentActivities(allActivities);
      } catch (error) {
        if (isAuthenticated) {
          // Only show error if still authenticated
          console.error("Error loading activities:", error);
          showError("Failed to load recent activities");
        }
      }
    };

    loadActivities();
  }, [notes, showError, isAuthenticated]); // Add isAuthenticated as dependency

  useEffect(() => {
    const loadTestCount = async () => {
      if (!isAuthenticated) return; // Don't load if not authenticated

      try {
        const results = await apiService.get("/api/test-results");
        setTestCount(results.length || 0);
      } catch (error) {
        console.error("Error loading test count:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTestCount();
  }, [isAuthenticated]); // Add isAuthenticated as dependency

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      setTestCount(0);
      setRecentActivities([]);
      setIsLoading(false);
    };
  }, []);

  // Add useEffect to close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Add useEffect to close sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // lg breakpoint
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let messageInterval;
    if (isProcessingVideo) {
      messageInterval = setInterval(() => {
        setCurrentVideoMessage((current) =>
          current === videoLoadingMessages.length - 1 ? 0 : current + 1
        );
      }, 3000); // Change message every 3 seconds
    }
    return () => clearInterval(messageInterval);
  }, [isProcessingVideo]);

  useEffect(() => {
    // Start with the entering animation
    setIsEntering(true);
    // Remove the animation state after it completes
    const timer = setTimeout(() => {
      setIsEntering(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <HamburgerButton onClick={toggleSidebar} isOpen={isSidebarOpen} />
      <main
        className={`transition-all duration-500 ease-in-out ${"ml-0 lg:ml-64"}`}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">
            Dashboard
          </h1>

          {/* Quick Summary Tool */}
          <div className="bg-dark-card rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-medium text-white">
                Quick Summary
              </h2>
              <SparklesIcon className="w-5 h-5 text-blue-500" />
            </div>
            <div className="space-y-4">
              <div className="flex flex-col space-y-4">
                {isProcessingFile || isSummarizing ? (
                  <div className="w-full h-24 sm:h-32 bg-dark-bg text-gray-200 rounded-lg px-4 py-3 flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                    <p className="text-sm text-gray-400">
                      {isProcessingFile
                        ? `Processing ${uploadedFileName}...`
                        : "Creating summary... This might take a minute"}
                    </p>
                    {summarizeInput.length > 5000 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Processing large content, please wait...
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-24 sm:h-32 bg-dark-bg text-gray-200 rounded-lg px-4 py-3 flex flex-col">
                    {uploadedFileName ? (
                      <div className="flex items-center h-full">
                        <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="text-gray-200 truncate">
                          {uploadedFileName}
                        </span>
                      </div>
                    ) : (
                      <textarea
                        value={summarizeInput}
                        onChange={(e) => setSummarizeInput(e.target.value)}
                        placeholder="Paste text to summarize..."
                        className="w-full h-full bg-transparent text-gray-200 text-base border-0 focus:outline-none focus:ring-0 resize-none"
                      />
                    )}
                  </div>
                )}

                {/* File Upload and Submit */}
                <div className="flex flex-wrap gap-2">
                  {/* Upload File button */}
                  <label className="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 bg-dark-hover text-gray-200 rounded-lg cursor-pointer hover:bg-dark-hover/70 transition-colors">
                    <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
                    <span>Upload File</span>
                    <input
                      type="file"
                      accept=".txt,.md,.pdf,.doc,.docx,.ppt,.pptx"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={isProcessingFile || isSummarizing}
                    />
                  </label>

                  {/* Add Video button */}
                  {/* <button
                    onClick={() => setShowVideoModal(true)}
                    className="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 bg-dark-hover text-gray-200 rounded-lg cursor-pointer hover:bg-dark-hover/70 transition-colors"
                  >
                    <VideoCameraIcon className="w-5 h-5 mr-2" />
                    <span>Add Video</span>
                  </button> */}

                  {/* Summarize button */}
                  <button
                    onClick={handleSummarize}
                    disabled={!summarizeInput.trim() || isSummarizing}
                    className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSummarizing ? "Summarizing..." : "Summarize"}
                  </button>

                  {/* Make Notes button */}
                  <button
                    onClick={handleMakeNotes}
                    disabled={!summarizeInput.trim() || isCreatingNotes}
                    className="w-full sm:w-auto px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreatingNotes ? "Creating..." : "Make Notes"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => navigate("/notes")}
              className="p-4 bg-dark-card rounded-xl hover:bg-dark-hover/50 transition-all duration-200"
            >
              <div className="flex items-center text-gray-400 hover:text-white">
                <div className="p-2 bg-blue-500/10 rounded-lg mr-3">
                  <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <h3 className="text-base sm:text-lg font-medium">Notes</h3>
                  <p className="text-sm opacity-75">
                    {notes.length} notes created
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/test")}
              className="p-4 bg-dark-card rounded-xl hover:bg-dark-hover/50 transition-all duration-200"
            >
              <div className="flex items-center text-gray-400 hover:text-white">
                <div className="p-2 bg-purple-500/10 rounded-lg mr-3">
                  <AcademicCapIcon className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <h3 className="text-base sm:text-lg font-medium">Tests</h3>
                  <p className="text-sm opacity-75">
                    {isLoading ? "Loading..." : `${testCount} tests taken`}
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Recent Activity */}
          <div className="bg-dark-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-medium text-white">
                Recent Activity
              </h2>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <ClockIcon className="w-5 h-5 text-green-500" />
              </div>
            </div>

            {recentActivities.length === 0 ? (
              <div className="text-center py-8">
                <div className="bg-dark-bg rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <DocumentTextIcon className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-gray-400">No recent activities</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivities.map((activity, index) => (
                  <div
                    key={index}
                    className="bg-dark-bg rounded-lg p-3 hover:bg-dark-hover/50 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`p-2 rounded-lg ${
                            activity.type === "note"
                              ? "bg-blue-500/10"
                              : "bg-purple-500/10"
                          }`}
                        >
                          {activity.type === "note" ? (
                            <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                          ) : (
                            <AcademicCapIcon className="w-4 h-4 text-purple-500" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-white font-medium">
                            {activity.title}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {activity.type === "note"
                              ? `Added to ${activity.folder || "Default"}`
                              : `Score: ${activity.score}%`}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(activity.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <VideoLoadingOverlay
        isVisible={isProcessingVideo}
        message={videoLoadingMessages[currentVideoMessage]}
        progress={progress}
      />

      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[0.5px] z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowVideoModal(false);
              setVideoUrl("");
            }
          }}
        >
          <div className="bg-dark-card rounded-2xl p-8 max-w-2xl w-full relative overflow-hidden">
            {/* Subtle gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5" />

            {/* Larger, more subtle animated circles */}
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-2xl animate-pulse" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/5 rounded-full blur-2xl animate-pulse delay-1000" />

            {/* Content with more spacing */}
            <div className="relative z-10 space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3">
                <VideoCameraIcon className="w-6 h-6 text-blue-500" />
                <h2 className="text-xl font-medium text-white">
                  Add YouTube Video
                </h2>
              </div>

              {/* Input section */}
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="Paste YouTube URL here..."
                    className="w-full bg-dark-bg/50 text-gray-200 px-4 py-3 rounded-lg border border-gray-800/50 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all duration-200 text-base"
                  />
                  {/* URL icon */}
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      if (!videoUrl.trim()) {
                        showError("Please enter a YouTube URL");
                        return;
                      }
                      try {
                        setIsProcessingVideo(true);
                        setShowVideoModal(false);
                        await handleVideoSummarize();
                      } finally {
                        setIsProcessingVideo(false);
                      }
                    }}
                    disabled={isProcessingVideo}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-base"
                  >
                    {isProcessingVideo ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      "Summarize Video"
                    )}
                  </button>

                  {/* Add Make Notes button */}
                  <button
                    onClick={async () => {
                      if (!videoUrl.trim()) {
                        showError("Please enter a YouTube URL");
                        return;
                      }
                      try {
                        setIsProcessingVideo(true);
                        setShowVideoModal(false);
                        const response = await apiService.summarizeYouTube(
                          videoUrl
                        );
                        if (!response || !response.summary) {
                          throw new Error("Failed to get video transcript");
                        }

                        // Use existing makeNotes functionality with the transcript
                        setIsCreatingNotes(true);
                        const notesResponse = await aiService.makeDetailedNotes(
                          response.summary
                        );

                        const noteData = {
                          title: response.title || "YouTube Video Notes",
                          content: notesResponse.mainSummary,
                          folder: "Default",
                          keyPoints: notesResponse.keyPoints || [],
                          source: videoUrl,
                        };

                        const newNote = await dataService.createNote(noteData);
                        if (!newNote || !newNote._id) {
                          throw new Error("Failed to create note");
                        }

                        setVideoUrl("");
                        setShowVideoModal(false);
                        showError("Video notes created!", "success");

                        // Navigate to the new note
                        await new Promise((resolve) =>
                          setTimeout(resolve, 100)
                        );
                        navigate(`/notes/${newNote._id}`);
                      } catch (error) {
                        console.error("Video processing error:", error);
                        showError(
                          error.message || "Failed to create video notes"
                        );
                      } finally {
                        setIsProcessingVideo(false);
                        setIsCreatingNotes(false);
                      }
                    }}
                    disabled={isProcessingVideo || isCreatingNotes}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-base"
                  >
                    {isCreatingNotes ? "Creating..." : "Make Notes"}
                  </button>

                  <button
                    onClick={() => {
                      setShowVideoModal(false);
                      setVideoUrl("");
                    }}
                    className="px-4 py-2.5 bg-dark-hover/50 text-gray-300 rounded-lg hover:bg-dark-hover transition-all duration-200 font-medium text-base"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
